import { memo, useReducer } from 'react';

import clsx from 'clsx';
import { SquarePen, Trash2 } from 'lucide-react';
import { isNonNullish, omit } from 'remeda';
import { toast } from 'sonner';
import useSWRMutation from 'swr/mutation';

import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';

import { PartnerForm } from '../../partnerFormDialog/form';
import { PartnerFormDialog } from '../../partnerFormDialog/PartnerFormDialog';

import css from './PartnerCard.module.css';

import { partnersService } from 'v2/api/partnersService';
import { Popconfirm } from 'v2/components/popconfirm/Popconfirm';
import { PromotionStatus } from 'v2/constants';
import { PartnerModel } from 'v2/types/PartnerModel';
import { fileSrc, handleHttpError } from 'v2/utils';

interface PartnerCardProps {
  partner: PartnerModel;
  onRevalidate: VoidFunction;
}

export const PartnerCard = memo<PartnerCardProps>(({ partner, onRevalidate }) => {
  const [isOpen, setOpen] = useReducer((state) => !state, false);

  const { trigger: triggerUpdate, isMutating } = useSWRMutation(
    ['updatePartner', partner.id],
    partnersService.updatePartner,
    {
      onSuccess: () => {
        onRevalidate();
        toast.success('The partner has been successfully updated');
        setOpen();
      },
      onError: handleHttpError,
    }
  );

  const { trigger: triggerDelete } = useSWRMutation(['deletePartner', partner.id], partnersService.deletePartner, {
    onSuccess: () => {
      onRevalidate();
    },
    onError: handleHttpError,
  });

  const handleSubmit = (values: PartnerForm, resetForm: VoidFunction) => {
    triggerUpdate(values).then(() => {
      resetForm();
    });
  };

  return (
    <div className={css.partnerCard}>
      <div style={{ backgroundImage: `url("${fileSrc(partner.iconId)}")` }} className={css.partnerCardPreviewContainer}>
        {isNonNullish(partner.promotionStatus) && partner.promotionStatus !== PromotionStatus.NO_STATUS && (
          <div
            className={clsx(
              css.partnerPromotionStatus,
              partner.promotionStatus === PromotionStatus.GOLD && css.gold,
              partner.promotionStatus === PromotionStatus.SILVER && css.silver,
              partner.promotionStatus === PromotionStatus.BRONZE && css.bronze,
              partner.promotionStatus === PromotionStatus.PLATINUM && css.platinum
            )}
          >
            {partner.promotionStatus}
          </div>
        )}

        <Chip className={css.partnerCardProvider} color="secondary" label={partner.providerName} size="small" />
      </div>

      <div className={css.partnerCardContent}>
        <div className={css.partnerCardContentInner}>
          <div className={css.partnerCardContentTitle}>
            <p className={css.partnerCardName}>{partner.name}</p>

            <Chip
              color={partner.isActive ? 'success' : 'default'}
              label={partner.isActive ? 'Active' : 'Inactive'}
              size="small"
            />
          </div>

          <p className={css.partnerCardDescription}>{partner.shortDescription}</p>
        </div>

        <div className={css.partnerCardActions}>
          <Popconfirm message="Are you sure that you want to delete this partner?" onConfirm={triggerDelete}>
            <Button startIcon={<Trash2 width={18} />} variant="outlined" color="error" fullWidth>
              Delete
            </Button>
          </Popconfirm>

          <Button onClick={setOpen} startIcon={<SquarePen width={18} />} variant="outlined" color="warning">
            Edit
          </Button>

          <PartnerFormDialog
            title="Edit Partner"
            isOpen={isOpen}
            onClose={setOpen}
            initialValues={omit(partner, ['id', 'order'])}
            isSubmitting={isMutating}
            onSubmit={handleSubmit}
          />
        </div>
      </div>
    </div>
  );
});

PartnerCard.displayName = 'PartnerCard';
